import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Segmented } from '../ui';
import { axiosInstance } from '../../utils/axiosInstance';
import {
  ErrorHistoryEntry,
  formatErrorCodeMessage,
  formatErrorOccurredAt,
} from './errorHistoryUtils';
import './statusHistoryModal.scss';

interface ErrorEntry {
  Machine: string;
  error_start_time: string;
  duration_seconds: number;
}

export interface StatusDetailLogEntry {
  id?: number;
  mac_id?: string;
  created_at: string;
  status?: string | null;
  prev_status?: string | null;
  is_door_open?: boolean | null;
  prev_is_door_open?: boolean | null;
  selected_cycle_id?: string | null;
  prev_selected_cycle_id?: string | null;
  selected_temperature_id?: string | null;
  prev_selected_temperature_id?: string | null;
  change_kinds?: string[] | null;
}

interface StatusHistoryModalProps {
  open: boolean;
  onClose: () => void;
  location: string;
  macId: string;
  machineName: string;
  on: (event: string, callback: (data: any) => void) => (() => void) | void;
  emit: (event: string, payload?: any) => void;
  isConnected?: boolean;
}

type ViewType = 'table' | 'graph';

function doorLabel(v: boolean | null | undefined): string {
  if (v === true) return 'open';
  if (v === false) return 'closed';
  return '—';
}

function formatChangeKinds(kinds: string[] | null | undefined): string {
  if (!kinds || kinds.length === 0) return '—';
  return kinds
    .map((k) => {
      if (k === 'status') return 'Status';
      if (k === 'door') return 'Door';
      if (k === 'cycle') return 'Cycle';
      return k;
    })
    .join(', ');
}

function formatDetailSummary(log: StatusDetailLogEntry): string {
  const kinds = log.change_kinds || [];
  const parts: string[] = [];

  if (kinds.includes('status')) {
    const from = log.prev_status || '—';
    const to = log.status || '—';
    parts.push(`${from} → ${to}`);
  }
  if (kinds.includes('door')) {
    parts.push(`Door ${doorLabel(log.prev_is_door_open)} → ${doorLabel(log.is_door_open)}`);
  }
  if (kinds.includes('cycle')) {
    const fromCycle = log.prev_selected_cycle_id || '—';
    const toCycle = log.selected_cycle_id || '—';
    const fromTemp = log.prev_selected_temperature_id;
    const toTemp = log.selected_temperature_id;
    const from =
      fromTemp && fromCycle !== '—' ? `${fromCycle}/${fromTemp}` : fromCycle;
    const to = toTemp && toCycle !== '—' ? `${toCycle}/${toTemp}` : toCycle;
    parts.push(`Cycle ${from} → ${to}`);
  }

  if (parts.length === 0) {
    if (log.status) parts.push(String(log.status));
  }
  return parts.join(' · ') || '—';
}

const StatusHistoryModal = ({
  open,
  onClose,
  location,
  macId,
  machineName,
  on,
  emit,
  isConnected,
}: StatusHistoryModalProps) => {
  const [errors, setErrors] = useState<ErrorEntry[]>([]);
  const [errorHistory, setErrorHistory] = useState<ErrorHistoryEntry[]>([]);
  const [detailLogs, setDetailLogs] = useState<StatusDetailLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorHistoryLoading, setErrorHistoryLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [viewType, setViewType] = useState<ViewType>('table');

  // Reset state whenever a new machine is opened, and stop history updates on close/unmount
  useEffect(() => {
    if (!open) return;
    setErrors([]);
    setErrorHistory([]);
    setDetailLogs([]);
    setLoading(true);
    setErrorHistoryLoading(true);
    setDetailLoading(true);
    setViewType('table');

    return () => {
      if (isConnected) {
        emit('monitor-history-stop');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, macId]);

  useEffect(() => {
    if (!open || !macId || !location || !isConnected) return;

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    const formattedStartDate = startDate.toISOString().split('T')[0];

    emit('monitor-history-client', {
      startDate: formattedStartDate,
      endDate: `${new Date().toISOString().split('T')[0]}`,
      siteId: location,
      Machine: macId,
      limit: 10,
    });
  }, [open, location, macId, emit, isConnected]);

  useEffect(() => {
    if (!open || !macId) return;
    let cancelled = false;
    setDetailLoading(true);

    (async () => {
      try {
        const { data } = await axiosInstance.get<{
          mac_id?: string;
          logs?: StatusDetailLogEntry[];
        }>(`/health/machines/${encodeURIComponent(macId)}/status-detail-logs`, {
          params: { days: 7 },
        });
        if (!cancelled) {
          setDetailLogs(Array.isArray(data?.logs) ? data.logs : []);
        }
      } catch {
        if (!cancelled) setDetailLogs([]);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, macId]);

  useEffect(() => {
    if (!open || !on) return;

    const handleMonitorHistory = (data: ErrorEntry[]) => {
      setLoading(false);
      setErrors(Array.isArray(data) ? data : []);
    };

    const handleMonitorError = () => {
      setLoading(false);
      setErrorHistoryLoading(false);
    };

    const handleMachineErrorHistory = (data: ErrorHistoryEntry[]) => {
      setErrorHistoryLoading(false);
      setErrorHistory(Array.isArray(data) ? data : []);
    };

    const cleanupMonitorHistory = on('monitor-history', handleMonitorHistory);
    const cleanupMonitorError = on('monitor-error', handleMonitorError);
    const cleanupMachineErrorHistory = on('error-logs', handleMachineErrorHistory);

    return () => {
      try {
        if (typeof cleanupMonitorHistory === 'function') cleanupMonitorHistory();
        if (typeof cleanupMonitorError === 'function') cleanupMonitorError();
        if (typeof cleanupMachineErrorHistory === 'function') cleanupMachineErrorHistory();
      } catch {
        // Ignore cleanup errors
      }
    };
  }, [open, on]);

  const formatDuration = (seconds: number) => {
    if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}m ${secs}s`;
    }
    return `${Math.floor(seconds)}s`;
  };

  const chartData = (Array.isArray(errors) ? errors : [])
    .filter((error) => error.duration_seconds)
    .map((error) => ({
      time: moment(error.error_start_time).format('DD/MM HH:mm'),
      duration: Number(error.duration_seconds) || 0,
      fullTime: moment(error.error_start_time).format('YYYY-MM-DD HH:mm:ss'),
      machine: error.Machine,
    }))
    .sort((a, b) => new Date(a.fullTime).getTime() - new Date(b.fullTime).getTime());

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <span>Machine history — {machineName || macId}</span>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <section className="status-history-section">
          <div className="status-history-section-header">
            <h3>Activity (last 7 days)</h3>
          </div>

          {detailLoading ? (
            <div className="status-history-empty">Loading activity…</div>
          ) : detailLogs.length === 0 ? (
            <div className="status-history-empty">
              No status, door, or cycle changes recorded.
            </div>
          ) : (
            <>
              <div className="status-history-count">
                {detailLogs.length} entr{detailLogs.length === 1 ? 'y' : 'ies'}
              </div>
              <div className="status-history-table">
                <div className="status-history-table-row status-history-table-head status-history-activity-row">
                  <div>When</div>
                  <div>Changed</div>
                  <div>Detail</div>
                </div>
                {detailLogs.map((log, index) => (
                  <div
                    key={`detail-${log.id ?? index}`}
                    className="status-history-table-row status-history-activity-row"
                  >
                    <div>{moment(log.created_at).format('DD/MM HH:mm:ss')}</div>
                    <div>{formatChangeKinds(log.change_kinds)}</div>
                    <div>{formatDetailSummary(log)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        <section className="status-history-section">
          <div className="status-history-section-header">
            <h3>Time offline (last 7 days)</h3>
            {!loading && errors.length > 0 && (
              <Segmented
                label="Show as"
                value={viewType}
                onChange={setViewType}
                options={[
                  { id: 'table', label: 'Table' },
                  { id: 'graph', label: 'Graph' },
                ]}
              />
            )}
          </div>

          {loading ? (
            <div className="status-history-empty">Loading history…</div>
          ) : errors.length === 0 ? (
            <div className="status-history-empty">No downtime recorded in the last 7 days.</div>
          ) : (
            <>
              <div className="status-history-count">{errors.length} entr{errors.length === 1 ? 'y' : 'ies'}</div>
              {viewType === 'table' ? (
                <div className="status-history-table">
                  <div className="status-history-table-row status-history-table-head">
                    <div>Machine</div>
                    <div>Time</div>
                    <div className="align-right">Duration</div>
                  </div>
                  {errors.map((error, index) => (
                    <div key={`${error.Machine}-${index}`} className="status-history-table-row">
                      <div>{error.Machine}</div>
                      <div>{moment(error.error_start_time).format('LTS')}</div>
                      <div className="align-right">
                        {error.duration_seconds ? formatDuration(Number(error.duration_seconds)) : '—'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="status-history-graph">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid vertical={false} stroke="#e1e7ee" />
                      <XAxis
                        dataKey="time"
                        interval="preserveStartEnd"
                        minTickGap={24}
                        tickLine={false}
                        axisLine={{ stroke: '#c8d1dc' }}
                        tick={{ fontSize: 11, fill: '#5a6b80' }}
                      />
                      <YAxis
                        width={44}
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 11, fill: '#5a6b80' }}
                        tickFormatter={(v: number) => formatDuration(v)}
                      />
                      <Tooltip
                        cursor={{ fill: 'rgba(1, 66, 106, 0.06)' }}
                        formatter={(value: number) => [`${formatDuration(value)}`, 'Time offline']}
                        labelFormatter={(label) => `${label}`}
                      />
                      <Bar dataKey="duration" fill="#01426a" name="Time offline" radius={[4, 4, 0, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </section>

        <section className="status-history-section">
          <div className="status-history-section-header">
            <h3>Error history (last 10)</h3>
          </div>

          {errorHistoryLoading ? (
            <div className="status-history-empty">Loading error history…</div>
          ) : errorHistory.length === 0 ? (
            <div className="status-history-empty">No error history recorded.</div>
          ) : (
            <>
              <div className="status-history-count">
                {errorHistory.length} entr{errorHistory.length === 1 ? 'y' : 'ies'}
              </div>
              <div className="status-history-table">
                <div className="status-history-table-row status-history-table-head">
                  <div>Machine</div>
                  <div>Timestamp</div>
                  <div className="align-right">Error code / message</div>
                </div>
                {errorHistory.map((error, index) => (
                  <div key={`error-history-${error.id || index}`} className="status-history-table-row">
                    <div>{String(error.machine_name || error.machineid || machineName)}</div>
                    <div>{formatErrorOccurredAt(error)}</div>
                    <div className="align-right">{formatErrorCodeMessage(error)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusHistoryModal;
